import React, { useState } from 'react';
import { User, Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const LoginPage: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);

    const { login, isLoading } = useAuth();

    // Handle login
    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!username || !password) {
            setError('Please enter both username and password');
            return;
        }

        const success = await login(username, password, rememberMe);
        if (!success) {
            setError('Invalid username or password. Please try again.');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <motion.div className="w-full max-w-md" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
                    {/* Logo and Header */}
                    <div className="text-center mb-4">
                        <div className="mx-auto mb-4 flex justify-center h-28">
                            <img src="/assets/ic_logo.png" alt="Drishti Logo" className="h-full w-full object-contain" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Admin Panel</h2>
                        <p className="text-gray-600 text-sm">Enter your credentials to access the dashboard</p>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-5" autoComplete="off">
                        {/* Username Field */}
                        <div className="space-y-2">
                            <label htmlFor="username" className="text-sm font-medium text-gray-700 block">
                                Username
                            </label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    id="username"
                                    name="username-field"
                                    type="text"
                                    placeholder="Enter username"
                                    value={username}
                                    onChange={(e) => {
                                        setUsername(e.target.value);
                                        setError('');
                                    }}
                                    className="w-full h-10 pl-10 pr-4 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition-all"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck="false"
                                    data-form-type="other"
                                />
                            </div>
                        </div>

                        {/* Password Field */}
                        <div className="space-y-2">
                            <label htmlFor="password" className="text-sm font-medium text-gray-700 block">
                                Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    id="password"
                                    name="password-field"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Enter password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setError('');
                                    }}
                                    className="w-full h-10 pl-10 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition-all"
                                    autoComplete="new-password"
                                    data-form-type="other"
                                />
                                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" tabIndex={-1}>
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <motion.div className="p-3 bg-red-50 border border-red-200 rounded-lg" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}>
                                <p className="text-sm text-red-600">{error}</p>
                            </motion.div>
                        )}

                        {/* Remember Me */}
                        <div className="flex items-center justify-between">
                            <label className="inline-flex items-center space-x-2 select-none cursor-pointer">
                                <input type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                                <span className="text-sm text-gray-700">Remember me</span>
                            </label>
                        </div>

                        {/* Login Button */}
                        <button
                            type="submit"
                            className="w-full h-10 bg-brand hover:bg-brand-hover text-white px-4 rounded-md text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all"
                            disabled={isLoading || !username || !password}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Logging in...
                                </>
                            ) : (
                                'Login'
                            )}
                        </button>
                    </form>
                </div>

                {/* Powered by ChainCode */}
                <div className="mt-6 flex justify-center h-8">
                    {/* <a href="https://www.chaincodeconsulting.com" target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-80 transition-opacity">
                        <img src="/assets/powered-by-chaincode.png" alt="Powered by ChainCode Consulting" className="h-10 w-auto" />
                    </a> */}
                    <img src="/assets/ic_drishti_logo.png" alt="Drishti Logo" className="w-auto" />
                </div>
            </motion.div>
        </div>
    );
};

export default LoginPage;
